import styled from '@emotion/styled';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { navigationService } from 'src/config/rootService';
import { Button } from 'src/shared/components/Button';
import { color, font, mediaQueries } from 'src/shared/styles/variables';

import { config } from 'src/config/config';
import appStoreButton from 'src/public/img/btn-appstore.svg';
import googlePlayButton from 'src/public/img/btn-google-play.svg';
import logoImage from 'src/public/img/mh-logo-text.svg';
import mainImage from 'src/public/img/missing_availability_info.svg';

const Wrapper = styled.div({
    position: 'absolute',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    overflowY: 'auto',
    top: 0,
    left: 0,
    height: '100%',
    width: '100%',
    backgroundImage: `radial-gradient(circle at 50% 0, ${color.white}, #ddedff)`,
});

const ContentWrapper = styled.div({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: 10,
});

const LogoImage = styled.img({
    position: 'absolute',
    top: 0,
    left: 0,
    padding: 25,
    cursor: 'pointer',
});

const HeadingText = styled.h4(
    {
        marginTop: 45,
        color: color.pickledBluewood,
        textAlign: 'center',
    },
    font.black(font.size.header),
);

const DescriptionText = styled.p(
    {
        color: color.steelGray,
        textAlign: 'center',
    },
    font.book(font.size.large),
);

const ButtonsWrapper = styled.div({
    display: 'flex',
    justifyContent: 'center',
    marginTop: '2em',
});

const StoreButton = styled.img({
    cursor: 'pointer',
    [mediaQueries.sm]: {
        width: 150,
    },
});

const MailLink = styled.a(
    {
        color: color.royalBlue,
    },
    font.heavy(font.size.medium),
);

const HelpText = styled.p(
    {
        marginTop: 50,
        color: color.steelGray,
        marginBottom: '2.5rem',
        textAlign: 'center',
    },
    font.medium(font.size.medium),
);

const CtaButtonWrapper = styled.div({
    button: {
        padding: '0.88rem',
    },
});

interface Props {
    hasCalendarSetup: boolean;
    isFirstSession?: boolean;
    onCtaClick?: () => void;
    isRoster: boolean;
}

export const CoachMissingAvailabilityInfo: React.FC<Props> = ({
    onCtaClick,
    hasCalendarSetup,
    isFirstSession,
    isRoster,
}) => {
    const { t } = useTranslation();
    const navigateToDashboard = () => {
        navigationService.navigate('/dashboard');
    };

    const handleStoreButtonClick = (link: string) => () => {
        window.open(link, '_blank');
    };

    const shouldRematch = hasCalendarSetup && isFirstSession;
    const heading = t(
        `videoSessions:${
            isRoster
                ? 'coach_missing_availability.header.calendar_outside'
                : hasCalendarSetup
                ? 'coach_missing_availability.header.calendar_full'
                : 'coach_missing_availability.header.calendar_not_connected'
        }`,
    );

    const downloadAppDescription = `coach_missing_availability.download_app.${
        isFirstSession ? 'first' : 'next'
    }`;

    const description = t(
        `videoSessions:${
            isRoster
                ? 'coach_missing_availability.description.download_app'
                : shouldRematch
                ? 'coach_missing_availability.description.another_provider'
                : downloadAppDescription
        }`,
    );

    return (
        <Wrapper>
            <LogoImage src={logoImage} onClick={navigateToDashboard} />

            <ContentWrapper>
                <img src={mainImage} alt="" />

                <HeadingText>{heading}</HeadingText>
                <DescriptionText>{description}</DescriptionText>

                {(!shouldRematch || isRoster) && (
                    <ButtonsWrapper>
                        <StoreButton
                            src={appStoreButton}
                            style={{ marginRight: 15 }}
                            onClick={handleStoreButtonClick(config.iOSAppStoreURL)}
                            data-testid="apple_store_button"
                        />
                        <StoreButton
                            src={googlePlayButton}
                            onClick={handleStoreButtonClick(config.AndroidGooglePlayStoreURL)}
                            data-testid="google_play_button"
                        />
                    </ButtonsWrapper>
                )}

                {onCtaClick && (
                    <CtaButtonWrapper>
                        <Button
                            onClick={onCtaClick}
                            marginTop={25}
                            fullWidth
                            withShadow
                            testID="cta_button"
                            width="normal"
                            noHorizontalPadding
                            wrapWhiteSpace
                        >
                            {isRoster && t('insuranceForm:insurance_approved.back_home')}

                            {!isRoster && t('videoSessions:coach_missing_availability.cta')}
                        </Button>
                    </CtaButtonWrapper>
                )}

                <HelpText>
                    <Trans
                        t={t}
                        ns="videoSessions"
                        i18nKey="coach_missing_availability.contact"
                        components={{
                            email: <MailLink href="mailto:help@joinmodernhealth.com" />,
                        }}
                    />
                </HelpText>
            </ContentWrapper>
        </Wrapper>
    );
};
